// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { forEach } from "lodash";
import { UIMessages } from "@config/constant";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { AccountState, AccountType, BankAccount, State } from "@utils/types";

const initialState: BankAccountState = {
  state: "idle",
  submitState: "idle",
  stateMessage: null,
  errorMessage: null,
  salaryAccounts: null,
  reimbursementAccounts: null,
  consultancyAccounts: null,
  bankAccountHistory: [],
};

interface BankAccountState {
  state: "failed" | "success" | "loading" | "idle";
  submitState: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  salaryAccounts: BankAccount | null;
  reimbursementAccounts: BankAccount | null;
  consultancyAccounts: BankAccount | null;
  bankAccountHistory: BankAccount[];
}

interface BankAccountInterface {
  count: number;
  bankAccounts: BankAccount[];
}

// For Payload
interface BankAccountChangeRequestPayloadInterface {
  accountName: string;
  accountNumber: string;
  accountType: string;
  bankAddress: string;
  bankCode: string;
  bankLocation: string;
  bankName: string;
  bankSwiftCode: string;
  beneficiaryAddress: string;
  branchCode: string;
  branchName: string;
  effectiveFrom: string;
  employeeEmail: string;
}

interface BankAccountChangeResponseInterface {
  applicationID: number;
}

export interface UpdateBankAccount {
  accountId: number;
  newStatus: string;
  action: string;
  reason?: string | null;
}

export const handleAccountChange = createAsyncThunk(
  "bankAccount/handleAccountChange",
  async ({ accountId, action, reason }: UpdateBankAccount, { dispatch }) => {
    const payload = {
      rejectionReason: reason || "",
    };
    return new Promise<{ requestResponse: BankAccountChangeResponseInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .post(
            `${AppConfig.serviceUrls.bankAccounts}/${accountId}/${action}`,
            payload
          )
          .then((resp) => {
            if (resp.status === 200) {
              dispatch(
                enqueueSnackbarMessage({
                  message: resp.data.message,
                  type: "success",
                })
              );
              resolve({ requestResponse: resp.data });
            } else {
              reject(resp.data.message);
            }
          })
          .catch((resp) => {
            dispatch(
              enqueueSnackbarMessage({
                message: resp.response?.data?.message,

                type: "error",
              })
            );
            reject((resp: { response: { data: { message: any } } }) => {
              enqueueSnackbarMessage({
                message: resp.response?.data?.message,
                type: "error",
              });
            });
          });
      }
    );
  }
);

export const createBankAccount = createAsyncThunk(
  "bankAccount/createBankAccount",
  async (payload: BankAccountChangeRequestPayloadInterface, { dispatch }) => {
    return new Promise<{ requestResponse: BankAccountChangeResponseInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .post(AppConfig.serviceUrls.bankAccounts, payload)
          .then((resp) => {
            if (resp.status === 201) {
              dispatch(
                enqueueSnackbarMessage({
                  message:
                    "Successfully requested the " +
                    payload.accountType.toLocaleLowerCase() +
                    " bank account change!",
                  type: "success",
                })
              );
              resolve({ requestResponse: resp.data });
            } else {
              reject(resp.data.message);
            }
          })
          .catch((resp) => {
            dispatch(
              enqueueSnackbarMessage({
                message: resp.response?.data?.message,
                type: "error",
              })
            );
            reject((resp: { response: { data: { message: any } } }) => {
              enqueueSnackbarMessage({
                message: resp.response?.data?.message,
                type: "error",
              });
            });
          });
      }
    );
  }
);

export const getActiveBankAccountData = createAsyncThunk(
  "bankAccount/getActiveBankAccountData",
  async (employeeEmail: string, { dispatch }) => {
    return new Promise<{ activeBankAccounts: BankAccountInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(
            AppConfig.serviceUrls.bankAccounts +
              "?employeeWorkEmail=" +
              employeeEmail +
              "&statusArray=" +
              AccountState.ACTIVE
          )
          .then((resp) => {
            if (resp.status === 200) {
              resolve({ activeBankAccounts: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    );
  }
);

export const getRequestedSalaryBankAccountData = createAsyncThunk(
  "bankAccount/getRequestedSalaryBankAccountData",
  async (_, { dispatch }) => {
    return new Promise<{ bankAccounts: BankAccountInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(
            AppConfig.serviceUrls.bankAccounts +
              "?statusArray=" +
              AccountState.REQUESTED +
              "&accountTypesArray=" +
              AccountType.SALARY
          )
          .then((resp) => {
            if (resp.status === 200) {
              resolve({ bankAccounts: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    );
  }
);

export const getAllBankAccountData = createAsyncThunk(
  "bankAccount/getAllBankAccountData",
  async (employeeEmail: string | undefined) => {
    return new Promise<{ bankAccounts: BankAccountInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(
            AppConfig.serviceUrls.bankAccounts +
              "?employeeWorkEmail=" +
              employeeEmail
          )
          .then((resp) => {
            if (resp.status === 200) {
              resolve({ bankAccounts: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    );
  }
);

export const deactivateAccount = createAsyncThunk(
  "bankAccount/deactivateAccount",
  async (accountId: number, { dispatch, rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().post(
        `${AppConfig.serviceUrls.bankAccounts}/${accountId}/deactivate`
      );
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarMessage({
            message: "Account deactivated successfully!",
            type: "success",
          })
        );
        return accountId;
      } else {
        return rejectWithValue("Failed to deactivate account.");
      }
    } catch (error: any) {
      dispatch(
        enqueueSnackbarMessage({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "An error occurred"
      );
    }
  }
);

export interface BankAccountFilter {
  createdFrom: string | null;
  createdTo: string | null;
  accountTypesArray: string[] | null;
  statusArray: string[] | null;
}

export const getFilteredBankAccounts = createAsyncThunk(
  "bankAccount/getFilteredBankAccounts",
  async (
    { filterArguments }: { filterArguments: BankAccountFilter },
    { dispatch }
  ) => {
    const queryString = Object.entries(filterArguments)
      .map(([key, value]) =>
        value
          ? typeof value === "string"
            ? `${key}=${value}`
            : `${value.map(
                (val: any, index: number) =>
                  `${index > 0 ? "&" : ""}${key}=${val}`
              )}`.replace(",", "")
          : ""
      )
      .filter(Boolean)
      .join("&");

    return new Promise<{ bankAccounts: BankAccountInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(AppConfig.serviceUrls.bankAccounts + "?" + queryString)
          .then((resp) => {
            if (resp.status === 200) {
              resolve({ bankAccounts: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    );
  }
);

export const BankAccountSlice = createSlice({
  name: "bankAccount",
  initialState,
  reducers: {
    resetBankAccounts: (state) => {
      state.salaryAccounts = null;
      state.reimbursementAccounts = null;
      state.consultancyAccounts = null;
      state.bankAccountHistory = [];
      state.state = State.idle;
    },

    resetSubmitState(state) {
      state.submitState = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActiveBankAccountData.pending, (state) => {
        state.state = "loading";
        state.stateMessage = UIMessages.loading.checkActiveBankInformation;
      })
      .addCase(getActiveBankAccountData.fulfilled, (state, action) => {
        const accounts: BankAccount[] =
          action.payload.activeBankAccounts.bankAccounts;

        forEach(accounts, (account: BankAccount) => {
          if (account.accountType.valueOf() === AccountType.SALARY) {
            state.salaryAccounts = account;
          } else if (account.accountType === AccountType.REIMBURSEMENT) {
            state.reimbursementAccounts = account;
          } else if (account.accountType === AccountType.CONSULTANCY) {
            state.consultancyAccounts = account;
          }
        });

        state.state = "success";
        state.stateMessage = UIMessages.success.bankAccountActiveFetch;
      })
      .addCase(getActiveBankAccountData.rejected, (state) => {
        state.state = "failed";
        state.stateMessage = UIMessages.error.activeBankAccounts;
      })
      .addCase(createBankAccount.pending, (state) => {
        state.submitState = "loading";
        state.stateMessage = UIMessages.loading.submitChangeRequest;
      })
      .addCase(createBankAccount.fulfilled, (state) => {
        state.submitState = "success";
        state.stateMessage = UIMessages.success.bankAccountSubmit;
      })
      .addCase(createBankAccount.rejected, (state) => {
        state.submitState = "failed";
        state.stateMessage = UIMessages.error.bankAccountSubmit;
      })
      .addCase(getAllBankAccountData.pending, (state) => {
        state.state = "loading";
        state.stateMessage = UIMessages.loading.checkAllBankInformation;
      })
      .addCase(getAllBankAccountData.fulfilled, (state, action) => {
        const accounts: BankAccount[] =
          action.payload.bankAccounts.bankAccounts;
        state.bankAccountHistory = accounts;

        state.state = "success";
        state.stateMessage = UIMessages.success.allBankAccountFetch;
      })
      .addCase(getAllBankAccountData.rejected, (state) => {
        state.state = "failed";
        state.stateMessage = UIMessages.error.allBankAccounts;
      })
      .addCase(handleAccountChange.pending, (state) => {
        state.submitState = "loading";
        state.stateMessage = UIMessages.loading.updateBankAccountStatus;
      })
      .addCase(handleAccountChange.fulfilled, (state) => {
        state.submitState = "success";
        state.stateMessage = UIMessages.success.bankAccountUpdateStatus;
      })
      .addCase(handleAccountChange.rejected, (state) => {
        state.submitState = "failed";
        state.stateMessage = UIMessages.error.updateBankAccountStatus;
      })
      .addCase(getRequestedSalaryBankAccountData.pending, (state) => {
        state.state = "loading";
        state.stateMessage = UIMessages.loading.loadingRequestedBankAccounts;
      })
      .addCase(getRequestedSalaryBankAccountData.fulfilled, (state, action) => {
        const accounts: BankAccount[] =
          action.payload.bankAccounts.bankAccounts;
        state.bankAccountHistory = accounts;

        state.state = "success";
        state.stateMessage = UIMessages.success.bankAccountsRequestedFetch;
      })
      .addCase(getRequestedSalaryBankAccountData.rejected, (state) => {
        state.state = "failed";
        state.stateMessage = UIMessages.error.requestedBankAccounts;
      })
      .addCase(getFilteredBankAccounts.pending, (state) => {
        state.state = "loading";
        state.stateMessage = UIMessages.loading.filteredBankAccounts;
      })
      .addCase(getFilteredBankAccounts.fulfilled, (state, action) => {
        const accounts: BankAccount[] =
          action.payload.bankAccounts.bankAccounts;
        state.bankAccountHistory = accounts;

        state.state = "success";
        state.stateMessage = UIMessages.success.filteredBankAccounts;
      })
      .addCase(getFilteredBankAccounts.rejected, (state) => {
        state.state = "failed";
        state.stateMessage = UIMessages.error.filteredBankAccounts;
      })
      .addCase(deactivateAccount.pending, (state) => {
        state.submitState = "loading";
        state.stateMessage = "Deactivating account...";
      })
      .addCase(deactivateAccount.fulfilled, (state, action) => {
        state.submitState = "success";
        state.stateMessage = "Account deactivated successfully!";
      })
      .addCase(deactivateAccount.rejected, (state, action) => {
        state.submitState = "failed";
        state.stateMessage = action.payload as string;
      });
  },
});

export const { resetBankAccounts, resetSubmitState } = BankAccountSlice.actions;

export default BankAccountSlice.reducer;
